import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import type { CellAnnotationCellKey, CellAnnotationState } from 'components/CellAnnotationValueViewer/types';
import { stateToWireEntry } from 'components/CellAnnotationValueViewer/utils/serialize';
import type { CellAnnotationStore, WireByCellKey } from './types';

const buildInitialStateForConfig = (config: {
  rowMode: CellAnnotationState['rowMode'];
  columns: CellAnnotationState['columnsConfig'];
}): CellAnnotationState => {
  const rows: CellAnnotationState['rows'] = [];
  if (config.rowMode === 'multi_row') {
    rows.push({ position: 0, value: null, metadata: {}, isPlaceholder: true });
  } else {
    rows.push({ position: 0, value: null, metadata: {}, isPlaceholder: false });
  }
  return {
    rowMode: config.rowMode,
    columnsConfig: config.columns,
    rows,
    isDirty: false,
  };
};

const useCellAnnotationStore = create<CellAnnotationStore>()(
  devtools(
    (set, get) => ({
      states: {},
      openPopouts: {} as Record<string, boolean>,

      hydrate: statesByCellKey => set(() => ({ states: { ...statesByCellKey } }), false, 'hydrate'),

      seedIfMissing: (cellKey, config) =>
        set(
          state => {
            if (state.states[cellKey]) {
              return state;
            }
            return {
              states: {
                ...state.states,
                [cellKey]: buildInitialStateForConfig(config),
              },
            };
          },
          false,
          'seedIfMissing'
        ),

      setRows: (cellKey, rows) =>
        set(
          state => {
            const existing = state.states[cellKey];
            if (!existing) {
              return state;
            }
            return {
              states: {
                ...state.states,
                [cellKey]: {
                  ...existing,
                  rows,
                  isDirty: true,
                },
              },
            };
          },
          false,
          'setRows'
        ),

      clearDirty: cellKeyPrefix =>
        set(
          state => ({
            states: Object.fromEntries(
              Object.entries(state.states).map(([key, value]) => {
                if (cellKeyPrefix && !key.startsWith(cellKeyPrefix)) {
                  return [key, value];
                }
                return [key, { ...value, isDirty: false }];
              })
            ),
          }),
          false,
          'clearDirty'
        ),

      isAnyDirty: () => Object.values(get().states).some(s => s.isDirty),

      collectDirtyPayload: () => {
        const out: WireByCellKey = {};
        Object.entries(get().states).forEach(([cellKey, state]) => {
          if (!state.isDirty) {
            return;
          }
          out[cellKey as CellAnnotationCellKey] = stateToWireEntry(state);
        });
        return out;
      },

      openPopout: cellKey =>
        set(
          state => ({
            openPopouts: { ...state.openPopouts, [cellKey]: true },
          }),
          false,
          'openPopout'
        ),

      closePopout: cellKey =>
        set(
          state => {
            if (!state.openPopouts[cellKey]) {
              return state;
            }
            const next = { ...state.openPopouts };
            delete next[cellKey];
            return { openPopouts: next };
          },
          false,
          'closePopout'
        ),
    }),
    { name: 'CellAnnotationStore' }
  )
);

export default useCellAnnotationStore;
